import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Breadcrumbs,
  Link,
  Avatar,
  Divider,
  Paper
} from '@mui/material';
import styled from '@emotion/styled';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import ChatIcon from '@mui/icons-material/Chat';
import CategoryIcon from '@mui/icons-material/Category';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { articles } from '../data/articles';
import HeaderArticle from './HeaderArticle';
import Footer from './Footer';

// Styled components
const ArticleSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  padding: '100px 0',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    padding: '70px 0',
  }
}));

const BreadcrumbsSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#f9f9f9',
  padding: '15px 0',
  borderBottom: '1px solid #eee'
}));

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(/assets/images/blog/inner/style1/4.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: '100px 0',
  position: 'relative',
  color: '#fff',
  textAlign: 'center',
  marginBottom: '50px'
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  fontSize: '42px',
  fontWeight: 700,
  lineHeight: 1.2,
  maxWidth: '800px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    fontSize: '32px',
  }
}));

const ArticleImage = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: '30px',
  borderRadius: '5px',
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: 'auto',
    display: 'block',
  }
}));

const ArticleMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginBottom: '20px',
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    marginRight: '20px',
    marginBottom: '10px',
    color: theme.palette.text.secondary,
    fontSize: '14px',
    '& svg': {
      fontSize: '16px',
      marginRight: '5px',
    }
  }
}));

const ArticleContent = styled(Box)(({ theme }) => ({
  '& h3': {
    marginTop: '30px',
    marginBottom: '15px',
  },
  '& p': {
    marginBottom: '20px',
    lineHeight: '1.8',
  },
  '& ul.dots': {
    paddingLeft: '20px',
    marginBottom: '20px',
    '& li': {
      marginBottom: '10px',
    }
  },
  '& blockquote': {
    padding: '30px',
    backgroundColor: '#f9f9f9',
    borderLeft: `5px solid ${theme.palette.primary.main}`,
    margin: '30px 0',
    fontStyle: 'italic',
    '& cite': {
      display: 'block',
      marginTop: '10px',
      fontSize: '14px',
      color: theme.palette.text.secondary,
    }
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '5px',
    marginBottom: '20px',
  }
}));

const SidebarWidget = styled(Paper)(({ theme }) => ({
  padding: '25px',
  marginBottom: '30px',
  borderRadius: '5px',
}));

const WidgetTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  paddingBottom: '15px',
  marginBottom: '20px',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    height: '3px',
    width: '50px',
    backgroundColor: theme.palette.primary.main,
  }
}));

const RecentPostItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginBottom: '15px',
  padding: '15px 0',
  borderBottom: '1px solid #eee',
  '&:last-child': {
    borderBottom: 'none',
    marginBottom: 0,
    paddingBottom: 0,
  }
}));

const PostImage = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  minWidth: '80px', // Ensure consistent width
  marginRight: '15px',
  borderRadius: '5px',
  overflow: 'hidden',
  flexShrink: 0, // Prevent image from shrinking
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block', // Remove any potential spacing issues
  }
}));

function Article() {
  const { id } = useParams();
  const article = articles.find(article => article.id === id);

  if (!article) {
    return (
      <>
        <HeaderArticle />
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Container>
            <Typography variant="h3">Article not found</Typography>
            <Box sx={{ mt: 3 }}>
              <Link component={RouterLink} to="/#rs-blog">Back to Blog</Link>
            </Box>
          </Container>
        </Box>
        <Footer />
      </>
    );
  }

  return (
    <>
      <HeaderArticle />
      <BreadcrumbsSection>
        <Container>
          <Breadcrumbs aria-label="breadcrumb">
            <Link component={RouterLink} to="/" sx={{ textDecoration: 'none', color: 'primary.main' }}>Home</Link>
            <Link component={RouterLink} to="/#rs-blog" sx={{ textDecoration: 'none', color: 'primary.main' }}>Blog</Link>
            <Typography color="text.secondary">{article.title}</Typography>
          </Breadcrumbs>
        </Container>
      </BreadcrumbsSection>
      
      <HeroSection>
        <Container>
          <HeroTitle variant="h1">{article.title}</HeroTitle>
        </Container>
      </HeroSection>

      <ArticleSection>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              {/* Title is now in the hero section */}

              <ArticleMeta>
                <Box>
                  <CalendarTodayIcon />
                  {article.date}
                </Box>
                <Box>
                  <PersonIcon />
                  {article.author}
                </Box>
                <Box>
                  <CategoryIcon />
                  {article.category}
                </Box>
                <Box>
                  <ChatIcon />
                  {article.comments} Comments
                </Box>
              </ArticleMeta>

              <ArticleImage>
                <img src={article.image} alt={article.title} />
              </ArticleImage>

              <ArticleContent>
                {article.content.map((item, index) => {
                  switch (item.type) {
                    case 'paragraph':
                      return <Typography key={index} paragraph>{item.text}</Typography>;
                    case 'heading':
                      return <Typography key={index} variant="h3">{item.text}</Typography>;
                    case 'quote':
                      return (
                        <blockquote key={index}>
                          <Typography>{item.text}</Typography>
                          {item.author && <cite>{item.author}</cite>}
                        </blockquote>
                      );
                    case 'list':
                      return (
                        <ul key={index} className="dots">
                          {item.items.map((listItem, i) => (
                            <li key={i} dangerouslySetInnerHTML={{ __html: listItem }} />
                          ))}
                        </ul>
                      );
                    case 'image':
                      return <img key={index} src={item.src} alt={item.alt || ''} />;
                    default:
                      return null;
                  }
                })}
              </ArticleContent>

              <Divider sx={{ my: 5 }} />

              {/* Comment section could be added here */}
            </Grid>

            <Grid item xs={12} md={4}>
              <SidebarWidget elevation={1}>
                <WidgetTitle variant="h5">Recent Posts</WidgetTitle>
                {articles
                  // Parse dates and sort from newest to oldest
                  .sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    return dateB - dateA;
                  })
                  // Limit to 5 posts
                  .slice(0, 5)
                  .map((post) => (
                    <RecentPostItem key={post.id}>
                      <PostImage>
                        <img src={post.image} alt={post.title} />
                      </PostImage>
                      <Box>
                        <Typography 
                          variant="subtitle1" 
                          component={RouterLink} 
                          to={`/article/${post.id}`}
                          sx={{ 
                            textDecoration: 'none', 
                            color: 'text.primary',
                            display: 'block',
                            fontWeight: 500,
                            '&:hover': {
                              color: 'primary.main'
                            }
                          }}
                        >
                          {post.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <CalendarTodayIcon sx={{ fontSize: 12, mr: 0.5 }} />
                          {post.date}
                        </Typography>
                      </Box>
                    </RecentPostItem>
                  ))}
              </SidebarWidget>
            </Grid>
          </Grid>
        </Container>
      </ArticleSection>
      <Footer />
    </>
  );
}

export default Article;
